import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { PACKAGES, ADD_ONS } from "@/data/site";
import { scrollTo } from "./Navigation";

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="bg-[#FDFBF7] py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-20">
          <p className="overline">Investment</p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-4">
            Considered packages.
            <br />
            <span className="italic font-light">Honestly priced.</span>
          </h2>
          <p className="text-[#4A4A4A] text-base md:text-lg leading-relaxed mt-6">
            Three tiers designed to fit the scale and intimacy of your story.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-black/10">
          {PACKAGES.map((pkg, idx) => (
            <motion.div
              key={pkg.name}
              data-testid={`pricing-pkg-${pkg.name.toLowerCase()}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: idx * 0.12 }}
              className={`border-r border-b border-black/10 p-8 md:p-10 ${
                pkg.featured ? "bg-[#F2EFE9]" : "bg-transparent"
              }`}
            >
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="font-serif-display text-3xl md:text-4xl">
                  {pkg.name}
                </h3>
                {pkg.featured && (
                  <span className="overline text-[#C5A059]">Most loved</span>
                )}
              </div>
              <div className="h-px w-12 bg-[#C5A059] my-6" />

              <div className="space-y-8">
                {pkg.rows.map((row) => (
                  <div key={row.location} className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <p className="font-serif-display text-xl">
                        {row.location}
                      </p>
                      <p className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A]">
                        {row.price}
                      </p>
                    </div>
                    <p className="text-xs uppercase tracking-[0.18em] text-[#4A4A4A]">
                      {row.duration}
                    </p>
                    <dl className="grid grid-cols-2 gap-y-2 text-sm text-[#4A4A4A] pt-2">
                      <dt className="opacity-70">Photos</dt>
                      <dd className="text-right">{row.photos}</dd>
                      <dt className="opacity-70">Outfits</dt>
                      <dd className="text-right">{row.outfits}</dd>
                      <dt className="opacity-70">People</dt>
                      <dd className="text-right">{row.people}</dd>
                      <dt className="opacity-70">Extra Person</dt>
                      <dd className="text-right text-[#C5A059]">{row.extra}</dd>
                    </dl>
                  </div>
                ))}
              </div>

              <div className="mt-10 space-y-3">
                <Link
                  to={`/book?pkg=${pkg.name.toLowerCase()}-${pkg.rows[0].location.toLowerCase()}`}
                  data-testid={`pricing-pay-${pkg.name.toLowerCase()}`}
                  className="block w-full py-3 text-center text-xs uppercase tracking-[0.24em] bg-[#1A1A1A] text-white hover:bg-[#C5A059] transition-colors duration-500"
                >
                  Book Now
                </Link>
                <button
                  data-testid={`pricing-cta-${pkg.name.toLowerCase()}`}
                  onClick={() => scrollTo("contact")}
                  className="w-full py-3 text-xs uppercase tracking-[0.24em] border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#F2EFE9] transition-colors duration-500"
                >
                  Inquire First
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Add-ons */}
        <div
          id="addons"
          data-testid="addons-section"
          className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-12 gap-10"
        >
          <div className="md:col-span-4">
            <p className="overline">Add-ons</p>
            <h3 className="font-serif-display text-3xl md:text-4xl leading-tight mt-4">
              Elevate your
              <br />
              <span className="italic font-light">experience.</span>
            </h3>
            <p className="text-[#4A4A4A] mt-6 max-w-sm leading-relaxed">
              Optional finishing touches. Albums are hand-bound and delivered
              within three weeks of your gallery.
            </p>
          </div>
          <ul className="md:col-span-8 divide-y divide-black/10 border-t border-black/10">
            {ADD_ONS.map((a) => (
              <li
                key={a.label}
                data-testid={`addon-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-baseline justify-between py-6"
              >
                <span className="font-serif-display text-lg md:text-xl">
                  {a.label}
                </span>
                <span className="text-sm md:text-base text-[#C5A059] tracking-wide">
                  {a.value}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
